<template>
  <BaseAlert
    v-if="focusedProducts.length === 0 || subscription === null"
    state="info"
    size="small"
  >
    Connect to the Internet to load your subscription and unlock the appropriate
    features.
  </BaseAlert>
  <PlansSkeleton v-else-if="isRefreshing" />
  <div v-else class="subscription">
    <!-- Not amazing to include this here, but it would otherwise require to have a separate
     component just for the subscription status i.e one line. -->
    <div class="subscription-status">
      <p v-if="subscription?.status === 'trialing'">
        Your
        <template v-if="subscription?.product"
          ><b>{{ subscription.product.name }}</b>
        </template>
        trial will end on
        <span class="canceled">{{ formattedEndDate }}</span>
      </p>
      <p v-else-if="subscription?.status === 'active'">
        Your
        <template v-if="subscription?.quantity && !isPersonalOrganization"
          ><b>{{ subscription.quantity }}-seat</b>
        </template>
        {{ subscription?.price?.interval === "month" ? "monthly" : "yearly" }}
        subscription will renew on <b>{{ formattedEndDate }}</b>
      </p>
      <p v-else-if="subscription?.status === 'canceled'">
        Your subscription has been <span class="canceled">canceled</span> and
        will end on <b>{{ formattedEndDate }}</b
        >.
      </p>
    </div>
    <div class="toolbar">
      <BaseButtonGroup>
        <template #firstButton>
          <BaseButton
            kind="main"
            type="secondary"
            text="Monthly"
            :state="interval === 'month' ? 'active' : 'default'"
            @click="interval = 'month'"
          />
        </template>
        <template #lastButton>
          <BaseButton
            kind="main"
            type="secondary"
            text="Yearly"
            :state="interval === 'year' ? 'active' : 'default'"
            @click="interval = 'year'"
          />
        </template>
      </BaseButtonGroup>
    </div>

    <div class="plans" :class="{ compact: mode === 'compact' }">
      <template v-for="product in focusedProducts" :key="product.id">
        <Plan
          v-if="product.plan === 'free'"
          :key="product.id"
          :layout="layout"
          :mode="mode"
          :can-manage="isAdmin"
          :org-id="organizationId"
          :product="product"
          :subscription="subscription"
        />
        <Plan
          v-for="price in computePrices(product)"
          v-else
          :key="price.id"
          :layout="layout"
          :mode="mode"
          :can-manage="isAdmin"
          :org-id="organizationId"
          :product="product"
          :price="price"
          :subscription="subscription"
          :preselected="preselectedPlan === product.name"
        />
      </template>
    </div>

    <div class="confetti-container">
      <ConfettiExplosion
        v-if="boom"
        :stage-height="800"
        :duration="5000"
        :particle-size="8"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import ConfettiExplosion from "vue-confetti-explosion";

import type { FullOrganization } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseButtonGroup from "@/components/base/BaseButtonGroup.vue";
import * as inject from "@/components/inject";
import Plan from "@/components/settings/Plan.vue";
import PlansSkeleton from "@/components/settings/PlansSkeleton.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import type {
  BillingInterval,
  BillingPrice,
  BillingProduct,
} from "@/utils/billing";
import { FORCED_FREEMIUM } from "@/utils/billing";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    BaseButtonGroup,
    ConfettiExplosion,
    Plan,
    PlansSkeleton,
  },
  props: {
    layout: {
      type: String as PropType<"default" | "vertical">,
      required: false,
      default: "default",
    },
    mode: {
      type: String as PropType<"expanded" | "compact">,
      required: false,
      default: "expanded",
    },
    filteredOut: {
      type: Array as PropType<Array<string>>,
      required: false,
    },
    preselectedPlan: {
      type: String,
      required: false,
    },
    /** The organization the plans are displayed for. */
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const controller = inject.controller();
    const billing = inject.billing();
    const releaser = useReleaser();
    const organizationId = computed(() => props.orgId);
    const openUrl = (url: string) => openExternalUrl(url, controller);

    // Set up a listener so that every time the user comes back to the page,
    // we refresh the subscription.
    //
    // Note that we would ideally want the controller to provide a reactive
    // value for the productsion, prices and subscription but it is not the case.
    //
    // Using the focus to refresh is a workaround.
    {
      const handleFocus = async () => {
        await billing.value?.refreshSubscriptions();
      };

      onMounted(() => {
        window.addEventListener("focus", handleFocus);
      });
      releaser.defer(() => {
        window.removeEventListener("focus", handleFocus);
      });
    }

    const products = computed(() => billing.value?.products?.value ?? []);
    const freeProduct = computed(
      () => billing.value?.freeProduct?.value ?? null
    );

    // Fetch current subscription
    const subscription = computed(() => {
      const orgId = organizationId.value;
      if (!orgId) return null;
      const sub = billing.value?.getSubscription(orgId);
      return sub?.value;
    });
    const subscribedPrice = computed(() => subscription.value?.price);

    // Load organization details for permission checks (admin vs member).
    const organizationIdRef = computed(() => organizationId.value);
    const organization = await releaser.add(
      bindAsync(organizationIdRef, async (id) =>
        id
          ? controller.organization.get(id)
          : releasableRef<FullOrganization | null>(null)
      )
    );

    const isPersonalOrganization = computed(() => {
      const orgId = organizationId.value;
      const personalOrgId =
        billing.value?.personalOrganizationId?.value ?? null;
      return !!orgId && orgId === personalOrgId;
    });

    const isAdmin = computed(() => {
      if (isPersonalOrganization.value) return true;
      const permissions = organization.value?.permissions ?? [];
      // Admin-like capabilities in this org; members typically have none.
      return permissions.includes("workspace-create");
    });

    // Whenever the plan transitions to premium, we show a confetti explosion.
    //
    // Watch the plan string (not the subscription object): the subscription
    // is rebuilt with a new identity on every profile re-emission, and
    // refreshing subscriptions from this watcher would re-trigger it,
    // causing an infinite auth.refresh loop. Refreshes are already handled
    // on mount and on window focus.
    const boom = ref(false);
    releaser.defer(
      watch(
        () => subscription.value?.product?.plan,
        (plan, previousPlan) => {
          if (plan === "premium" && previousPlan !== undefined) {
            boom.value = true;
          }
        }
      )
    );

    // Force refresh subscription every time the user does visit the Plans page
    // but do so asynchonously not to block the UI
    billing.value?.refreshSubscriptions();

    // Build the list of products/prices to display
    const filterProducts = (
      products: BillingProduct[],
      interval: BillingInterval
    ) => {
      if (!products) {
        return [];
      }
      // Filter out the products that don't have a price with the given interval
      products = products.filter((_product: BillingProduct) =>
        _product.prices.find(
          (_price: BillingPrice) => _price.interval === interval
        )
      );
      // Filter out the products that don't have a price
      products = products.filter(
        (_product: BillingProduct) => _product.prices.length > 0
      );
      // Sort the products by amount, but put custom products at the end
      products.sort((a: BillingProduct, b: BillingProduct) => {
        // Get all prices for the interval and select the lowest one
        const aPrices = a.prices.filter(
          (price: BillingPrice) => price.interval === interval
        );
        const bPrices = b.prices.filter(
          (price: BillingPrice) => price.interval === interval
        );

        if (!aPrices.length || !bPrices.length) {
          return 0;
        }

        // Select the lowest price for each product
        const aPrice = aPrices.reduce(
          (min: BillingPrice, price: BillingPrice) =>
            price.amount < min.amount ? price : min
        );
        const bPrice = bPrices.reduce(
          (min: BillingPrice, price: BillingPrice) =>
            price.amount < min.amount ? price : min
        );

        const aIsCustom = aPrice.model === "custom";
        const bIsCustom = bPrice.model === "custom";
        // If one is custom and one is not, put custom at the end
        if (aIsCustom && !bIsCustom) {
          return 1;
        }
        if (!aIsCustom && bIsCustom) {
          return -1;
        }
        // If both are custom or both are not custom, sort by amount
        return aPrice.amount - bPrice.amount;
      });
      return products;
    };

    const isSeatBasedProductForInterval = (
      product: BillingProduct,
      interval: BillingInterval
    ) => {
      return product.prices.some(
        (price: BillingPrice) =>
          price.interval === interval && price.model === "seat"
      );
    };

    const isCustomProductForInterval = (
      product: BillingProduct,
      interval: BillingInterval
    ) => {
      return product.prices.some(
        (price: BillingPrice) =>
          price.interval === interval && price.model === "custom"
      );
    };

    const shouldIncludeProduct = (
      product: BillingProduct,
      interval: BillingInterval
    ) => {
      const isSeatBasedPrice = isSeatBasedProductForInterval(product, interval);
      const isCustomPrice = isCustomProductForInterval(product, interval);
      const isFree = product.plan === "free";

      if (props.filteredOut?.includes(product.name)) {
        return false;
      }

      // When freemium is off, the Free plan is only shown to users still on
      // it — it is no longer offered as a downgrade to anyone else.
      if (
        isFree &&
        !FORCED_FREEMIUM &&
        subscription.value?.product?.plan === "premium"
      ) {
        return false;
      }

      if (isPersonalOrganization.value) {
        return isFree || (!isSeatBasedPrice && !isCustomPrice);
      } else {
        return isFree || isSeatBasedPrice || isCustomPrice;
      }
    };

    const shouldExcludeBecauseFullyHiddenAndNotSubscribed = (
      product: BillingProduct
    ) => {
      // Free should always be included.
      if (product.plan === "free") return false;

      // If all prices are hidden AND the current subscription isn't one of them,
      // the product will render no visible cards (and should not affect "auto" mode).
      const prices = product.prices ?? [];
      if (prices.length === 0) return false;

      const allHiddenPrices = prices.every((p) => !p.visible);
      if (!allHiddenPrices) return false;

      const subscribedPriceId = subscribedPrice.value?.id ?? null;
      const isSubscribedToOneOfThesePrices =
        !!subscribedPriceId && prices.some((p) => p.id === subscribedPriceId);

      return !isSubscribedToOneOfThesePrices;
    };

    // Maintain a reactive list of filtered & sorted products by interval
    //
    // Note that those products have a .price field which contains *all* the prices, no
    // matter the interval.
    const monthlyProducts = computed(() => {
      const filteredPlans = filterProducts(products.value || [], "month");
      const allPlans = [freeProduct.value, ...filteredPlans];
      return allPlans.filter(
        (product: BillingProduct | null): product is BillingProduct =>
          !!product &&
          !shouldExcludeBecauseFullyHiddenAndNotSubscribed(product) &&
          shouldIncludeProduct(product, "month")
      );
    });

    const yearlyProducts = computed(() => {
      const filteredPlans = filterProducts(products.value || [], "year");
      const allPlans = [freeProduct.value, ...filteredPlans];
      return allPlans.filter(
        (product: BillingProduct | null): product is BillingProduct =>
          product !== null &&
          !shouldExcludeBecauseFullyHiddenAndNotSubscribed(product) &&
          shouldIncludeProduct(product, "year")
      );
    });

    // Monthly/yearly toggle
    const interval = ref<BillingInterval>(
      subscribedPrice.value?.interval || "year"
    );

    // Get the current plans based on the selected interval
    const focusedProducts = computed(() => {
      return interval.value === "month"
        ? monthlyProducts.value
        : yearlyProducts.value;
    });

    const focusedProductsCount = computed(() => focusedProducts.value.length);
    const isFewPlans = computed(() => focusedProductsCount.value <= 2);

    const formattedEndDate = computed(() => {
      return subscription.value?.period_end?.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    });

    // Return only the prices for a product that relate to a specific interval.
    const computePrices = (product: BillingProduct): BillingPrice[] => {
      // We know that the plans that have been filtered have at least one price, see above.
      return product.prices.filter(
        (price: BillingPrice) => price.interval === interval.value
      );
    };

    const isRefreshing = computed(
      () => billing.value?.isRefreshing.value ?? false
    );

    return {
      boom,
      isAdmin,
      isFewPlans,
      isPersonalOrganization,
      isRefreshing,
      organizationId,
      computePrices,
      focusedProducts,
      formattedEndDate,
      interval,
      openUrl,
      subscription,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/settings.scss";

.subscription-status {
  @extend %font-caption-regular;
  margin-bottom: 12px;

  @include themed() {
    color: t($grey-70);
  }

  p {
    margin: 0;
  }

  b {
    @extend %font-caption-medium;

    @include themed() {
      color: t($primary);
    }
  }
}

.toolbar {
  margin-bottom: 12px;
}

.plans {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &.compact {
    gap: 6px;
  }
}

.canceled {
  @include themed() {
    color: t($secondary-80);
  }
}

.confetti-container {
  position: fixed;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: $z-index-critical;
}
</style>
